import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN } from '~/consts/roles';

import { EnrolledTier, useEnrolLuxLoyaltyAccount } from '~/hooks/apis/loyalty/useEnrolLuxLoyaltyAccount';

import CustomerServiceRepSelector from './CustomerServiceRepSelector';
import LuxLoyaltyEnrolConfirmDialog from './LuxLoyaltyEnrolConfirmDialog';

const TIER_OPTIONS: Array<App.LuxLoyaltyTier> = ['bronze', 'silver', 'gold', 'platinum', 'infinite'];

interface Props {
  userId: string;
  userLabel: string;
  /** Called after the enrol succeeds and the loyalty-account query has been invalidated. */
  onEnrolled: (tier: EnrolledTier) => void;
}

type EnrolMode = 'default' | 'specify';

/**
 * Renders the Société enrolment form when a user has no loyalty account yet.
 * Gated to admin and loyalty-admin roles; non-permitted users see an info
 * banner instead of the form.
 */
export default function LuxLoyaltyEnrolPanel({ userId, userLabel, onEnrolled }: Props) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [mode, setMode] = useState<EnrolMode | null>(null);
  const [tier, setTier] = useState<App.LuxLoyaltyTier | null>(null);
  const [csRepId, setCsRepId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const enrolMutation = useEnrolLuxLoyaltyAccount({
    onSuccess: (enrolledTier) => {
      enqueueSnackbar(
        enrolledTier === 'default'
          ? `Enrolled ${userLabel} in Société (default tier)`
          : `Enrolled ${userLabel} in Société as ${enrolledTier}`,
        { variant: 'success' },
      );
      setConfirmOpen(false);
      setMode(null);
      setTier(null);
      setCsRepId(null);
      onEnrolled(enrolledTier);
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to enrol user: ${error.message}`, { variant: 'error' });
    },
  });

  const requiresCsRep = mode === 'specify' && tier === 'infinite';
  const canSubmit =
    (mode === 'default' || (mode === 'specify' && tier !== null)) && (!requiresCsRep || !!csRepId);

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    enrolMutation.mutate({
      customerId: userId,
      tier: mode === 'specify' && tier ? tier : undefined,
      customerServiceRepId: requiresCsRep && csRepId ? csRepId : undefined,
    });
  };

  const fallback = (
    <Alert severity="info">
      You don't have permission to enrol this customer in Société. This action is restricted to admin and loyalty-admin
      users.
    </Alert>
  );

  return (
    <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN]} fallback={fallback}>
      <Stack direction="column" gap={3} maxWidth={640}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Enrol customer in Société
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This customer is not yet a Société member. Choose an enrolment mode to add them to the programme.
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Enrolment mode *
          </Typography>
          <RadioGroup
            value={mode ?? ''}
            onChange={(e) => {
              const next = e.target.value as EnrolMode;
              setMode(next);
              if (next === 'default') {
                setTier(null);
              }
            }}
          >
            <FormControlLabel
              value="default"
              control={<Radio />}
              label="Default — let Société resolve the tier (currently Bronze unless a pre-handler matches)"
            />
            <FormControlLabel
              value="specify"
              control={<Radio />}
              label="Specify tier — enrol the customer directly at the chosen tier"
            />
          </RadioGroup>
        </Box>

        {mode === 'specify' && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Tier *
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {TIER_OPTIONS.map((tierOption) => {
                const palette = theme.palette.loyaltyTiers[tierOption];
                const color = palette?.main ?? theme.palette.grey[500];
                const isSelected = tier === tierOption;
                return (
                  <ButtonBase
                    key={tierOption}
                    onClick={() => setTier(tierOption)}
                    sx={{
                      minWidth: 110,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      border: 2,
                      borderColor: isSelected ? color : 'divider',
                      bgcolor: isSelected ? alpha(color, 0.08) : 'transparent',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      '&:hover': {
                        borderColor: color,
                        bgcolor: alpha(color, 0.04),
                      },
                    }}
                  >
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                    <Typography variant="subtitle2" fontWeight={isSelected ? 700 : 500}>
                      {tierOption.charAt(0).toUpperCase() + tierOption.slice(1)}
                    </Typography>
                    {isSelected && <CheckCircleOutlineIcon sx={{ fontSize: 16, color }} />}
                  </ButtonBase>
                );
              })}
            </Stack>
          </Box>
        )}

        {requiresCsRep && (
          <Box>
            <CustomerServiceRepSelector
              value={csRepId}
              onChange={setCsRepId}
              required
              error={!csRepId}
              helperText={
                csRepId
                  ? 'This rep will become the customer’s dedicated Infinite Concierge.'
                  : 'Infinite-tier customers must be linked to a dedicated rep.'
              }
            />
          </Box>
        )}

        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || enrolMutation.isPending}
          >
            Enrol customer
          </Button>
        </Box>
      </Stack>

      <LuxLoyaltyEnrolConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        userLabel={userLabel}
        tier={mode === 'specify' && tier ? tier : undefined}
        isSubmitting={enrolMutation.isPending}
      />
    </PermissionedComponent>
  );
}
